import React, { useContext, useMemo } from "react";
import { ModalBody, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ListItem } from "@/components/WebsitesAndAppsListModal/ListItem";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { BackgroundTab, RestrictedModalItem, WindowData } from "@/js/components/RegularClassroomUser/utils";
import { ClasswizeModal } from "@/components/ui/Modal";
import { ClassroomToolsContext } from "@/contexts/ClassroomToolsContext";
import { getTabUrl, getFaviconUrl, isInternalBrowserTab } from "@/components/WebsitesAndAppsListModal/utils";

type ActiveTab = WindowData & {
	id: string;
	active: boolean;
};

interface Props {
	title: string;
	items: BackgroundTab[];
	restrictedItems: RestrictedModalItem[];
	activeTab?: ActiveTab | null;
	studentUsername: string;
	open: boolean;
	canCloseTab: boolean;
	classIsActive: boolean;
	isMonitorMode: boolean;
	isStudentClaimed: boolean;
	forceCloseTab: (tabId: string, isTabActive: boolean) => void;
	onClose: () => void;
}

const POLICIES_FEATURE = "policies";

export function WebsitesAndAppsListModal(props: Props): JSX.Element {
	const { t } = useTranslation();
	const features = getFeatures();
	const context = useContext(ClassroomToolsContext);
	const policiesEnabled = features.includes(POLICIES_FEATURE);

	// Build list items using declarative approach
	const listItems = useMemo(() => {
		const items: JSX.Element[] = [];

		const handleOpenRuleModal = (url: string, signature: string): void => {
			context.openRuleModal(false, url, signature, props.studentUsername);
		};

		const handleForceCloseTab = (tabId: string, isActiveTab: boolean): void => {
			props.forceCloseTab(tabId, isActiveTab);
		};

		// Handle restricted items (priority display)
		if (props.restrictedItems.length > 0) {
			return props.restrictedItems.map((item, index) => {
				const url = getTabUrl(item);
				const stableKey = item.tab_id || `restricted-${index}`;
				const canApplyRule = "canApplyRule" in item ? item.canApplyRule : true;
				const canControl = canApplyRule && !isInternalBrowserTab(url);

				return (
					<ListItem
						key={stableKey}
						itemKey={stableKey}
						chromeTabId={item.tab_id || ""}
						title={item.title}
						faviconUrl={getFaviconUrl(item)}
						websiteUrl={url}
						handleOpenRuleModal={handleOpenRuleModal}
						canCloseTab={props.canCloseTab}
						forceCloseTab={(tabId: string) => handleForceCloseTab(tabId, false)}
						classIsActive={props.classIsActive}
						isMonitorMode={props.isMonitorMode}
						isStudentClaimed={props.isStudentClaimed}
						policiesEnabled={policiesEnabled}
						canApplyRule={canControl}
					/>
				);
			});
		}

		// Handle regular tabs (active tab + background tabs)
		if (props.activeTab) {
			const url = getTabUrl(props.activeTab);
			const canApplyRule = "canApplyRule" in props.activeTab ? (props.activeTab.canApplyRule as boolean) : true;
			const canControl = canApplyRule && !isInternalBrowserTab(url);

			items.push(
				<ListItem
					key={props.activeTab.tab_id}
					itemKey={props.activeTab.tab_id}
					chromeTabId={props.activeTab.tab_id}
					title={props.activeTab.title}
					faviconUrl={getFaviconUrl(props.activeTab)}
					websiteUrl={url}
					handleOpenRuleModal={handleOpenRuleModal}
					canCloseTab={props.canCloseTab}
					forceCloseTab={(tabId: string) => handleForceCloseTab(tabId, true)}
					classIsActive={props.classIsActive}
					isMonitorMode={props.isMonitorMode}
					isStudentClaimed={props.isStudentClaimed}
					policiesEnabled={policiesEnabled}
					canApplyRule={canControl}
					isActiveTab={true}
				/>
			);
		}

		// Add background tabs
		if (props.items && props.items.length > 0) {
			const backgroundItems = props.items.map((item, index) => {
				const url = getTabUrl(item);
				const canApplyRule = "canApplyRule" in item ? (item.canApplyRule as boolean) : true;
				const canControl = canApplyRule && !isInternalBrowserTab(url);

				return (
					<ListItem
						key={item.tab_id || `background-${index}`}
						itemKey={item.tab_id || `background-${index}`}
						chromeTabId={item.tab_id}
						title={item.title}
						faviconUrl={getFaviconUrl(item)}
						websiteUrl={url}
						handleOpenRuleModal={handleOpenRuleModal}
						canCloseTab={props.canCloseTab}
						forceCloseTab={(tabId: string) => handleForceCloseTab(tabId, false)}
						classIsActive={props.classIsActive}
						isMonitorMode={props.isMonitorMode}
						isStudentClaimed={props.isStudentClaimed}
						policiesEnabled={policiesEnabled}
						canApplyRule={canControl}
						isActiveTab={false}
					/>
				);
			});

			items.push(...backgroundItems);
		}

		return items;
	}, [props, context, policiesEnabled]);

	return (
		<ClasswizeModal size="xl" title={props.title} onClose={props.onClose} isOpen={props.open}>
			<ModalBody>
				{listItems.length === 0 ? (
					<Text textAlign="center" color="gray.500" py={4}>
						{t("websitesModal.noItems")}
					</Text>
				) : (
					<div>{listItems}</div>
				)}
			</ModalBody>
		</ClasswizeModal>
	);
}
